import {
	Badge,
	Box,
	Button,
	Card,
	HStack,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text
} from "@chakra-ui/react";
import LocalSpinner from "components/LocalSpinner";
import { ArrowRight } from "@phosphor-icons/react";
import { useQuery } from "react-query";
import { getWeaviateQuestion, getWeaviateSlice } from "api/ai/ai.service";
import questionService from "api/issb/question.service";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { H3 } from "components/text/headings";
import { QCardBody, QCardHeader } from "./IssbDataInput/components/QuestionCard";
import { useFoundationV2CuratedRowsForYear, useFoundationV2Years } from "hooks/query/issb";
import { useNavigate } from "react-router-dom";
import { routes } from "config/paths";
import { useRightSidebar } from "context/RightSidebarContext";
import { workstreamForFigure } from "utils/foundationWorkstreamLink";
import { resolveFoundationCitation } from "utils/foundationCitation";
import { getFormattedNumValue } from "utils";

/**
 * Shared citation content used by both the right sidebar panel and the modal.
 *
 * `foundation` is a row from `foundationSourceRows` — a Foundation Data v2 figure an answer was drafted from.
 * Unlike the other three kinds it is not a Weaviate lookup: the citation names a datapoint and a reporting
 * year, and the figure itself comes from that year's curated rows.
 */
function CitationContent({ citation, domElement, questionId, label, title, foundation }) {
  let dataUrl = citation && citation["data-url"];
  if ( ! label) label = domElement?.textContent;

  let weaviateQuestionId = citation && citation["data-question_id"];
  let weaviateSourceId = citation && citation["data-source_id"];

  const { data: question, isLoading: isLoadingQuestion } = useQuery({
    queryKey: ["question", weaviateQuestionId || questionId],
    queryFn: async () => {
		if ( ! questionId) {
		const wq = await getWeaviateQuestion(weaviateQuestionId);
		questionId = wq.questionId;
		}
      return questionService.get(questionId);
    },
    enabled: !dataUrl && !!(weaviateQuestionId || questionId),
    refetchInterval: 60_000,
  });

  const { data: slice, isLoading: isLoadingSlice } = useQuery({
    queryKey: ["slice", weaviateSourceId],
    queryFn: () => getWeaviateSlice(weaviateSourceId),
    enabled: !dataUrl && !weaviateQuestionId && !questionId && !!weaviateSourceId,
  });

  /*
    The year's approved figures — every workstream's, because a citation records a year and a datapoint uid
    and nothing about which workstream the figure came from. Fetched only when this is a foundation citation. `enabled` overrides the
    hook's own default (it spreads config last), so a citation with no year fires no request at all - there
    is nothing to look one up by, and guessing across years would put a fabricated figure under a real name.
  */
  const foundationYear = foundation?.year ?? null;
  const {
    data: curatedRows,
    isLoading: isLoadingFoundation,
    isError: isFoundationError,
  } = useFoundationV2CuratedRowsForYear(foundationYear, {
    enabled: !!foundation && foundationYear != null,
  });

  /*
    The workstream list, to link the figure to the Foundation Data page it was approved on. Only for a
    foundation citation; cached, so a second citation opened costs nothing.
  */
  const { data: foundationYears } = useFoundationV2Years({ enabled: !!foundation });
  const navigate = useNavigate();
  const { doClose } = useRightSidebar();

  /*
    Foundation Data v2 figures. An EARLY RETURN placed before the others on purpose: this function falls
    THROUGH to the question branch rather than requiring a match, so a block appended at the end would never
    be reached and would render an empty heading plus "Details unavailable".
  */
  if (foundation) {
    const { row, usedVersion, currentVersion, versionChanged } =
      resolveFoundationCitation(curatedRows, foundation);
    /*
      Laid out as the metric (i) overlay's header (FoundationDataV2's MetricInfoOverlay): uppercase category
      eyebrow, large name and value. The figure's provenance is not repeated here — it lives in that overlay.
    */
    const displayValue = row
      ? row.dataValue == null || row.dataValue === ""
        ? "Not reported"
        : row.valueNumeric != null
        ? getFormattedNumValue(row.valueNumeric, 2)
        : row.dataValue
      : null;
    const workstream = workstreamForFigure(foundationYears, row, foundation.year);
    const openWorkstream = () => {
      doClose?.();
      navigate(
        `${routes.foundationDataV2Answer}?id=${workstream.questionId}${
          workstream.entityId ? `&entity=${workstream.entityId}` : ""
        }`
      );
    };
    const groupLabel = row?.category ? row.category.charAt(0) + row.category.slice(1).toLowerCase() : "";
    return (
      <Box mt={4} bg="white" border="1px solid" borderColor="gray.200" borderRadius="16px" overflow="hidden">
        {/* The version this answer used, against the current one — first, because it changes how everything
            below should be read. The archived row cannot be fetched (GET /years/{year}/curated excludes
            archived rows), but the figure written into the answer records its value (data-foundation_figure).
            A citation reached from the Sources list carries no tag, so it falls back to the version alone. */}
        {row && versionChanged ? (
          <Box bg="orange.50" borderBottom="1px solid" borderColor="orange.200" borderLeft="4px solid" borderLeftColor="orange.400" px={5} py={4}>
            {foundation.citedFigure ? (
              <>
                <Text fontSize="11px" fontWeight="800" letterSpacing="0.08em" color="orange.700" textTransform="uppercase">
                  This answer cites version {usedVersion}
                </Text>
                <Text fontSize="22px" fontWeight="800" color="orange.800" sx={{ fontVariantNumeric: "tabular-nums" }} mt={1}>
                  {foundation.citedFigure}
                </Text>
                <Text fontSize="12.5px" color="gray.700" mt={1}>
                  Latest version is {currentVersion}, shown below.
                </Text>
              </>
            ) : (
              <>
                <Text fontSize="12.5px" fontWeight="700" color="orange.800">
                  Used version {usedVersion} · latest version is {currentVersion}
                </Text>
                <Text fontSize="12.5px" color="gray.700" mt={1}>
                  The figure below is the latest version, so it may not be the number this answer used. The
                  earlier version is archived and cannot be shown.
                </Text>
              </>
            )}
          </Box>
        ) : null}
        <Box px={5} pt={5} pb={row ? 5 : 4} borderBottom={row ? "none" : "1px solid"} borderColor="gray.200">
          {groupLabel ? (
            <Text fontSize="11px" fontWeight="800" letterSpacing="0.08em" color="gray.500" textTransform="uppercase">
              {groupLabel}
            </Text>
          ) : null}
          <Text fontSize="18px" fontWeight="800" color="darkTeal" letterSpacing="-0.3px" lineHeight="1.3">
            {foundation.label}
          </Text>
          <Text fontSize="12.5px" color="gray.500" mt={1}>
            Approved foundation data{foundation.year ? ` · ${foundation.year}` : ""}
          </Text>
          {row ? (
            <HStack align="baseline" spacing={2} mt={3} flexWrap="wrap">
              <Text fontSize="22px" fontWeight="800" color="darkTeal" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {displayValue}
              </Text>
              {row.unit ? (
                <Text fontSize="13px" fontWeight="600" color="gray.600">
                  {row.unit}
                </Text>
              ) : null}
              {currentVersion != null || usedVersion != null ? (
                <Badge variant="subtle" bg="gray.100" color="gray.600">
                  Version {currentVersion ?? usedVersion}
                </Badge>
              ) : null}
            </HStack>
          ) : null}
          {workstream ? (
            <Button
              mt={3}
              variant="link"
              size="sm"
              color="teal"
              fontWeight="600"
              rightIcon={<ArrowRight size={12} weight="bold" />}
              onClick={openWorkstream}
            >
              Open in Foundation Data
            </Button>
          ) : null}
        </Box>

        {/* Only the states with no figure to show: loading, no year, a failed read, a removed figure. */}
        {row ? null : (
        <Box px={5} py={5}>
          {isLoadingFoundation && <LocalSpinner />}
          {!foundation.year && (
            <Text fontSize="13px" color="gray.600">
              The reporting year was not recorded on this citation, so the approved figure cannot be looked up.
              {usedVersion ? ` The answer was written against version ${usedVersion}.` : ""}
            </Text>
          )}
          {foundation.year && isFoundationError && (
            <Text fontSize="13px" color="gray.600">
              The {foundation.year} foundation data could not be loaded.
            </Text>
          )}
          {foundation.year && !isLoadingFoundation && !isFoundationError && !row && (
            <Text fontSize="13px" color="gray.600">
              This figure is no longer in the {foundation.year} foundation data — it has been removed or
              replaced.
              {usedVersion ? ` The answer was written against version ${usedVersion}.` : ""}
            </Text>
          )}
        </Box>
        )}
      </Box>
    );
  }

  // URL citations (web search results)
  if (dataUrl) {
    if ( ! title) title = citation?.title;
    return (
      <Card p={5} mt={4}>
        <H3>{title || "Web Source"}</H3>
        <Link href={dataUrl} isExternal color="blue.500" fontSize="sm" mt={2} wordBreak="break-all">{dataUrl}</Link>
      </Card>
    );
  }

  // File/source citations (data-source_id = Weaviate slice UUID)
  if (weaviateSourceId && !weaviateQuestionId && !questionId) {
    if ( ! title) title = citation?.title || slice?.title;
    const page = citation?.["data-page"] || slice?.page;
    return (
      <>
        {isLoadingSlice && <LocalSpinner />}
        {!isLoadingSlice && !slice && (
          <Card p={5} mt={4}>
            <H3>{title || "Source"}</H3>
            {page && <Text fontSize="sm" color="gray.500">Page {page}</Text>}
            <Text mt={2}>Details unavailable</Text>
          </Card>
        )}
        {slice && (
          <Card p={5} mt={4}>
            <H3>{title || slice.fileName || "Source"}</H3>
            {slice.fileName && title && title !== slice.fileName && (
              <Text fontSize="sm" color="gray.500">{slice.fileName}</Text>
            )}
            {page && <Text fontSize="sm" color="gray.500">Page {page}</Text>}
            {slice.content && (
              <Text mt={3} fontSize="sm" whiteSpace="pre-wrap">{slice.content}</Text>
            )}
          </Card>
        )}
      </>
    );
  }

  // Question citations (data-question_id = Weaviate question UUID)
  if ( ! title) title = citation?.title || question?.title;

  return (
    <>
      <H3 mt={4}>{title}</H3>
      {isLoadingQuestion && <LocalSpinner />}
      {!isLoadingQuestion && !question && (
        <Card p={5}>
          <Text>Details unavailable</Text>
        </Card>
      )}
      {question && (
        <Card p={5}>
          <QCardHeader
            reference={question.issbReference}
            title={question.question}
            stage={question.stage}
            stageType={question.stageType}
            owner={question.owner}
            assignees={question.assignees}
            id={question.id}
          />
          <QCardBody q={question} showMultiPartAnswer={true} readOnly={true} />
        </Card>
      )}
    </>
  );
}

/**
 * Citation panel for the right sidebar (used by ReportDocEditorPanel).
 */
export default function CitationSidePanel({ citation, domElement, questionId, label, title, reportDoc, foundation }) {
  if ( ! label) label = domElement?.textContent;

  // No close button of its own: the right sidebar drawer already draws one, and two read as a mistake.
  return (
    <Box>
      <RightSidebarHeader>Citation {label}</RightSidebarHeader>
      <CitationContent citation={citation} domElement={domElement} questionId={questionId} label={label} title={title} foundation={foundation} />
    </Box>
  );
}

/**
 * Citation modal for use in AI chat (where the right sidebar is not available).
 */
export function CitationModal({ isOpen, onClose, citation, domElement }) {
  const label = domElement?.textContent;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Citation {label}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <CitationContent citation={citation} domElement={domElement} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
